import React, { useState } from 'react';
import { Box, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventNoteIcon from '@mui/icons-material/EventNote';
import VideocamIcon from '@mui/icons-material/Videocam';
import { PatientEncounter } from '../../services/api';
import { ClinicalChip, SourceChip } from './clinicalBadges';

type Props = {
  encounters: PatientEncounter[];
  loading?: boolean;
  /** Cap the number of encounters shown; the rest are reachable via onViewAll. */
  limit?: number;
  /** Renders a "View all in History" link when provided. */
  onViewAll?: () => void;
};

const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontWeight: 600,
  fontSize: '0.68rem',
  lineHeight: 1.2,
};

const fmtDate = (s?: string | null): string => {
  if (!s) return 'Undated';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// PF often packs "Migraines (Appt time: ...) (Arrival time: ...)" into the CC.
// Show the real complaint prominently and keep the logistics as a faint suffix.
const splitCC = (cc?: string | null): { main: string; meta: string } => {
  const t = (cc || '').trim();
  if (!t) return { main: '', meta: '' };
  const i = t.indexOf('(');
  if (i === 0) return { main: '', meta: t };
  if (i > 0) return { main: t.slice(0, i).trim(), meta: t.slice(i).trim() };
  return { main: t, meta: '' };
};

const NoteSection: React.FC<{ title: string; body?: string | null }> = ({ title, body }) => {
  if (!body) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ ...labelSx, color: 'text.secondary' }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary', mt: 0.25 }}
      >
        {body}
      </Typography>
    </Box>
  );
};

const EncounterRow: React.FC<{ enc: PatientEncounter; defaultOpen?: boolean; divider?: boolean }> = ({ enc, defaultOpen, divider }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const { main, meta } = splitCC(enc.chief_complaint);
  const hasNote =
    !!(enc.subjective || enc.objective || enc.assessment || enc.plan || enc.snapshot_diagnosis || enc.snapshot_medications);
  return (
    <Box sx={{ borderTop: divider ? '1px solid' : 'none', borderColor: 'divider' }}>
      <Box
        role={hasNote ? 'button' : undefined}
        onClick={hasNote ? () => setOpen((v) => !v) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          py: 0.6,
          cursor: hasNote ? 'pointer' : 'default',
          '&:hover': hasNote ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        <Box sx={{ color: '#0284c7', mt: 0.25, display: 'flex' }}>
          {enc.is_virtual ? <VideocamIcon fontSize="small" /> : <EventNoteIcon fontSize="small" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {fmtDate(enc.encounter_date)}
            </Typography>
            {enc.encounter_type && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                · {enc.encounter_type}
              </Typography>
            )}
            {enc.provider && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                · {enc.provider}
              </Typography>
            )}
            {enc.is_virtual && <ClinicalChip label="Telehealth" color="info" />}
            <SourceChip source={enc.source} />
          </Box>
          {(main || meta) && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {main && (
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {main}
                </Box>
              )}
              {meta && (
                <Box component="span" sx={{ color: 'text.disabled', ml: main ? 0.75 : 0, fontSize: '0.8em' }}>
                  {meta}
                </Box>
              )}
            </Typography>
          )}
        </Box>
        {hasNote && (
          <ExpandMoreIcon
            fontSize="small"
            sx={{ color: 'text.disabled', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
          />
        )}
      </Box>
      {hasNote && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ pl: 4, pb: 1.25, pt: 0 }}>
            <NoteSection title="Subjective" body={enc.subjective} />
            <NoteSection title="Objective" body={enc.objective} />
            <NoteSection title="Assessment" body={enc.assessment} />
            <NoteSection title="Plan" body={enc.plan} />
            <NoteSection title="Diagnoses (at visit)" body={enc.snapshot_diagnosis} />
            <NoteSection title="Medications (at visit)" body={enc.snapshot_medications} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const PatientEncountersPanel: React.FC<Props> = ({ encounters, loading, limit, onViewAll }) => {
  const shown = typeof limit === 'number' ? encounters.slice(0, limit) : encounters;
  const hiddenCount = encounters.length - shown.length;
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={labelSx}>
          Encounters / Visit Notes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {loading && <CircularProgress size={12} />}
          {!loading && encounters.length > 0 && (
            <Chip size="small" label={`${encounters.length}`} sx={{ height: 18, fontSize: '0.7rem' }} />
          )}
        </Box>
      </Box>
      {!loading && encounters.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          No encounters recorded
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {shown.map((enc, i) => (
            <EncounterRow key={enc.id} enc={enc} defaultOpen={i === 0 && shown.length === 1} divider={i > 0} />
          ))}
          {onViewAll && (
            <Typography
              component="button"
              type="button"
              onClick={onViewAll}
              sx={{
                alignSelf: 'flex-start',
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: 'primary.main',
                fontWeight: 600,
                fontSize: '0.8rem',
                px: 0,
                mt: 0.25,
              }}
            >
              {hiddenCount > 0 ? `View all ${encounters.length} in History →` : 'View all in History →'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PatientEncountersPanel;
